import { Controller } from "@hotwired/stimulus"
import slugify  from 'slugify'
import { get, post, put, patch, destroy } from '@rails/request.js'

export default class extends Controller {
  static targets = [ "imagePicker", "progressBar", "imagesList", "form", "slug", "price", "title", "tab", "tabContent", "submitEditing", "submitSaved" ]
  static classes = [ "tabActive" ]
  static values = { productId: Number, currentTab: Number, editing: Boolean }

  connect() {
    this.editingValue = false
  }

  editing() {
    if (!this.editingValue) {
      this.editingValue = true
    }
  }

  submitEnd(event) {
    // Keep the "Save changes" state when the frame re-renders from a failed
    // (422) response so the admin can correct the errors and retry.
    if (event.detail.success) {
      this.editingValue = false
    }
  }

  editingValueChanged() {
    if (this.editingValue) {
      this.submitEditingTarget.classList.remove('!hidden')
      this.submitSavedTarget.classList.add('!hidden')
    } else {
      this.submitEditingTarget.classList.add('!hidden')
      this.submitSavedTarget.classList.remove('!hidden')
    }
  }

  imagePickerTargetDisconnected() {
    if (this.uppy) {
      this.uppy.close()
    }
  }

  imagePickerTargetConnected() {
    this.startUppy()
  }

  updateSlug() {
    this.slugTarget.value = slugify(this.titleTarget.value, { lower: true, strict: true })
  }

  async startUppy() {
    const Uppy = (await import('@uppy/core')).default
    const XHRUpload = (await import('@uppy/xhr-upload')).default
    const DragDrop = (await import('@uppy/drag-drop')).default
    const ProgressBar = (await import('@uppy/progress-bar')).default

    this.uppy = new Uppy({ autoProceed: true })
          .use(
            DragDrop, { target: this.imagePickerTarget }
          )
          .use(
            XHRUpload, { endpoint: '/upload/product_images' }
          )
          .use(
            ProgressBar, { target: this.progressBarTarget, hideAfterFinish: false }
          )

    this.uppy.on('complete', (result) => {
      const filesAdded = result.successful.map((file) => file.response.body)

      this.addImages(filesAdded)
      this.uppy.reset()
    })
  }

  async addImages(images) {
    await post(`/admin/products/${this.productIdValue}/add-images`, {
      body: {
        files: images
      },
      responseKind: 'turbo-stream'
    })
  }

  async removeImage(event) {
    await post(`/admin/products/${this.productIdValue}/remove-image`, {
      body: {
        product_image_id: event.params.imageId
      },
      responseKind: 'turbo-stream'
    })
  }

  async editFile(event) {
    await post(`/admin/products/${this.productIdValue}/edit-file`, {
      body: {
        file_id: event.params.fileId
      },
      responseKind: 'turbo-stream'
    })
  }

  async updateFile(event) {
    await post(`/admin/products/${this.productIdValue}/update-file`, {
      body: {
        file_id: event.params.fileId,
        filename: event.params.filename,
        url: event.params.url,
      },
      responseKind: 'turbo-stream'
    })
  }

  async removeFile(event) {
    await post(`/admin/products/${this.productIdValue}/remove-file`, {
      body: {
        file_id: event.params.fileId
      },
      responseKind: 'turbo-stream'
    })
  }

  // updateImagesPreview() {
  //   const newImages = this.productImages.map((image, index) => {
  //     const imageEl = document.createElement('div')
  //     imageEl.innerHTML = `
  //     <img class="rounded w-48 h-48 object-cover" src="/uploads/cache/${image.id}" alt="">
  //     <button type="button" data-index="${index}" class="image-item-delete rounded-full h-6 w-6 bg-red-600 hover:bg-red-800 transition text-red-200 absolute top-0 right-0 -translate-y-1/2 translate-x-1/2 flex items-center justify-center">
  //       <iconify-icon icon="mdi-close"></iconify-icon>
  //     </button>
  //     `
  //     imageEl.classList.add('image-preview-item');
  //     return imageEl
  //   })
  //   this.imagesListTarget.replaceChildren(...newImages)
  //   this.imagesListTarget.querySelectorAll('.image-item-delete').forEach((deleteButton) => {
  //     deleteButton.addEventListener('click', (event) => {
  //       const index = event.currentTarget.dataset.index
  //       this.productImages.splice(index, 1)
  //       this.updateImagesPreview()
  //     })
  //   })
  // }

  updateBundleExclusive(event) {
    if (event.target.checked) {
      this.priceTarget.value = 0.0
      this.priceTarget.readonly = true
      this.priceTarget.classList.add('input-disable')
    } else {
      this.priceTarget.readonly = false
      this.priceTarget.classList.remove('input-disable')
    }
  }

  switchInfoTab() {
    this.currentTabValue = 0
  }

  switchImagesTab() {
    this.currentTabValue = 1
  }

  switchFilesTab() {
    this.currentTabValue = 2
  }

  switchBundledProductsTab() {
    this.currentTabValue = 2
  }

  currentTabValueChanged() {
      this.tabTargets.forEach((element, index) => {
        if (index !== this.currentTabValue) {
          element.classList.remove(this.tabActiveClass)
        } else {
          element.classList.add(this.tabActiveClass)
        }
      })

      this.tabContentTargets.forEach((element, index) => {
        if (index !== this.currentTabValue) {
          element.classList.add('hidden')
        } else {
          element.classList.remove('hidden')
        }
      })
  }

  disconnect() {
    if (this.uppy) {
      this.uppy.close()
    }
  }
}
